import { CloudIntegrationSyncJob } from '@cast/types-inventory';

import { ConnectionChoice, CONNECTING_CHECK_STEPS } from '../../../_constants';

const COMMITMENT_ASSET_TYPES = new Set([
  'RESERVED_INSTANCES',
  'SAVINGS_PLANS',
  'GCP_RESOURCE_CUDS',
  'GCP_FLEX_CUDS',
  'AZURE_RESERVATIONS',
  'AZURE_SAVINGS_PLANS',
  'AWS_CAPACITY_RESERVATIONS',
  'GCP_CAPACITY_RESERVATIONS',
]);

const isAssetTypeActive = (
  statuses: CloudIntegrationSyncJob[] | undefined,
  assetTypes: Set<string>
): boolean =>
  statuses?.some(
    (status) =>
      assetTypes.has(status.assetType ?? '') &&
      (status.status === 'SUCCESS' ||
        status.status === 'IN_PROGRESS' ||
        (status.syncedAssets ?? 0) > 0)
  ) ?? false;

const isAssetTypeReady = (
  statuses: CloudIntegrationSyncJob[] | undefined,
  assetTypes: Set<string>
): boolean =>
  statuses?.some(
    (status) =>
      assetTypes.has(status.assetType ?? '') &&
      (status.status === 'SUCCESS' || (status.syncedAssets ?? 0) > 0)
  ) ?? false;

const hasReliabilityMetricsSync = (
  statuses: CloudIntegrationSyncJob[] | undefined
): boolean => isAssetTypeActive(statuses, new Set(['RELIABILITY_METRICS']));

const hasReliabilityMetricsReady = (
  statuses: CloudIntegrationSyncJob[] | undefined
): boolean => isAssetTypeReady(statuses, new Set(['RELIABILITY_METRICS']));

export const getCloudConnectChecklistStep = ({
  hasIntegration,
  syncStatuses,
}: {
  hasIntegration: boolean;
  syncStatuses: CloudIntegrationSyncJob[] | undefined;
}): number => {
  if (!hasIntegration) {
    return 0;
  }

  let completedSteps = 0;

  if (isAssetTypeActive(syncStatuses, new Set(['CLUSTER']))) {
    completedSteps = 1;
  }

  if (
    isAssetTypeActive(
      syncStatuses,
      new Set(['INSTANCE', 'RELIABILITY_METRICS'])
    )
  ) {
    completedSteps = 2;
  }

  if (isAssetTypeActive(syncStatuses, COMMITMENT_ASSET_TYPES)) {
    completedSteps = CONNECTING_CHECK_STEPS.length;
  }

  return Math.min(completedSteps, CONNECTING_CHECK_STEPS.length);
};

const getSnapshotChecklistStep = ({
  assetListClusterCount,
  syncStatuses,
}: {
  assetListClusterCount: number;
  syncStatuses: CloudIntegrationSyncJob[] | undefined;
}): number => {
  if (assetListClusterCount > 0) {
    return CONNECTING_CHECK_STEPS.length;
  }

  if (hasReliabilityMetricsSync(syncStatuses)) {
    return CONNECTING_CHECK_STEPS.length - 1;
  }

  return 0;
};

export const getActiveChecklistStep = ({
  connectionChoice,
  hasIntegration,
  syncStatuses,
  assetListClusterCount,
  hasNewCluster,
}: {
  connectionChoice: ConnectionChoice;
  hasIntegration: boolean;
  syncStatuses: CloudIntegrationSyncJob[] | undefined;
  assetListClusterCount: number;
  hasNewCluster: boolean;
}): number => {
  if (connectionChoice === ConnectionChoice.single) {
    return hasNewCluster
      ? CONNECTING_CHECK_STEPS.length
      : CONNECTING_CHECK_STEPS.length - 1;
  }

  if (connectionChoice === ConnectionChoice.snapshot) {
    return getSnapshotChecklistStep({ assetListClusterCount, syncStatuses });
  }

  return getCloudConnectChecklistStep({ hasIntegration, syncStatuses });
};

export const isConnectionDataReady = ({
  connectionChoice,
  hasIntegration,
  syncStatuses,
  assetListClusterCount,
  hasNewCluster,
}: {
  connectionChoice: ConnectionChoice;
  hasIntegration: boolean;
  syncStatuses: CloudIntegrationSyncJob[] | undefined;
  assetListClusterCount: number;
  hasNewCluster: boolean;
}): boolean => {
  switch (connectionChoice) {
    case ConnectionChoice.cloudConnect:
      return (
        hasIntegration &&
        (assetListClusterCount > 0 ||
          isAssetTypeReady(syncStatuses, new Set(['CLUSTER', 'INSTANCE'])) ||
          isAssetTypeReady(syncStatuses, COMMITMENT_ASSET_TYPES))
      );
    case ConnectionChoice.snapshot:
      return (
        assetListClusterCount > 0 || hasReliabilityMetricsReady(syncStatuses)
      );
    case ConnectionChoice.single:
      return hasNewCluster;
    default:
      return false;
  }
};
